// Copy for the website imagery. See SiteHeaders.tsx.
//
// Taken from Chloe's Miro brief (Website imagery, 14 Oct 2026), one export per
// sticky. The words are starting points marketing can replace; the shapes are
// the thing being handed over.
//
// Nothing here can point at Tell Us Once: no part-filled claims, no "answer
// once". Every win is the care expert's.

/** The care expert in every picture. Jenny is the name the brief uses. */
export const EXPERT = {
  name: 'Jenny',
  role: 'Your care expert',
} as const;

export type ChatLine = { from: 'keni' | 'user'; text: string };

/**
 * One fragment. A picture is a list of these, so a new request is a content
 * edit rather than a new component.
 */
export type Piece =
  /** A push notification, as it lands. */
  | { kind: 'push'; title: string; body: string; when: string }
  /**
   * A thread with the care expert, on the chat's own gardenia ground.
   * `replies` are quick replies, `offer` a tappable row, `typing` the dots.
   */
  | {
      kind: 'chat';
      lines: ChatLine[];
      replies?: string[];
      offer?: string;
      typing?: boolean;
    }
  /** Bubbles on their own, no panel: lifted straight out of the thread. */
  | { kind: 'bubbles'; lines: ChatLine[]; replies?: string[] }
  /** A calendar pop up. */
  | {
      kind: 'reminder';
      day: string;
      month: string;
      title: string;
      time: string;
    }
  /** A single call to action. */
  | { kind: 'button'; label: string }
  /** The care expert's face, on a call. */
  | { kind: 'call' }
  /** A phone ringing: the care expert calling in. */
  | { kind: 'incoming' }
  /** The care expert's face with a new-activity badge and what it is. */
  | { kind: 'face'; title: string; body: string };

export type Picture = { id: string; label: string; pieces: Piece[] };

// Shared fragments, said once so two pictures cannot drift apart.

const FUNDING_APPROVED: Piece = {
  kind: 'push',
  title: 'Your funding is approved',
  body: 'Attendance Allowance: £110.40 a week, from 3 November.',
  when: 'now',
};

const HOMES_FOUND: Piece = {
  kind: 'push',
  title: 'We found 7 care homes for you',
  body: 'All within 5 miles, and all can meet your mum’s needs.',
  when: '2m ago',
};

const CALL_SOON: Piece = {
  kind: 'reminder',
  day: '21',
  month: 'Oct',
  title: `Call with ${EXPERT.name}`,
  time: 'In 10 minutes',
};

// ── 1. Main banners: home and pillar pages ────────────────────────────────

/**
 * How a banner is arranged round the blurred app. Pieces split into a column
 * either side of the phone, in order:
 *
 * - `classic`: a thread on the left; a push and a call tile on the right.
 * - `call`: a call tile on the left; loose bubbles and a push on the right.
 * - `cascade`: the phone pushed left, every piece in one column beside it.
 * - `homepage`: a thread and a button on the left; two updates on the right.
 */
export type BannerLayout = 'classic' | 'call' | 'cascade' | 'homepage';

export type BannerPage = {
  id: string;
  label: string;
  variants: { layout: BannerLayout; pieces: Piece[] }[];
};

/**
 * Three versions per page, so no two pages have to share a picture. Homepage
 * is a spread of what turns up; each sector page leads with a member saying
 * where they came from, and a face on a call.
 */
export const BANNERS: BannerPage[] = [
  {
    id: 'homepage',
    label: 'Homepage',
    variants: [
      {
        layout: 'homepage',
        pieces: [
          {
            kind: 'chat',
            lines: [
              {
                from: 'keni',
                text: 'Hi, I’ve looked at your mum’s case. There’s more help out there than you think.',
              },
            ],
          },
          { kind: 'button', label: 'Request a call' },
          FUNDING_APPROVED,
          {
            kind: 'reminder',
            day: '24',
            month: 'Oct',
            title: 'Bank statement needed',
            time: 'Asked by Sam in your care circle',
          },
        ],
      },
      {
        layout: 'call',
        pieces: [
          { kind: 'call' },
          {
            kind: 'bubbles',
            lines: [
              {
                from: 'keni',
                text: `Hi, I’m ${EXPERT.name}. I’ll be looking after you and your mum from here.`,
              },
              { from: 'user', text: 'Thank you. That’s such a relief.' },
            ],
          },
          HOMES_FOUND,
        ],
      },
      {
        layout: 'cascade',
        pieces: [
          {
            kind: 'chat',
            lines: [
              {
                from: 'keni',
                text: 'I’ve found three things you could claim for your mum.',
              },
            ],
            offer: `Book a call with ${EXPERT.name}`,
          },
          FUNDING_APPROVED,
          {
            kind: 'reminder',
            day: '24',
            month: 'Oct',
            title: 'Bank statement needed',
            time: 'Asked by Sam in your care circle',
          },
        ],
      },
    ],
  },
  {
    id: 'employers',
    label: 'Employers',
    variants: [
      {
        layout: 'classic',
        pieces: [
          {
            kind: 'chat',
            lines: [
              {
                from: 'user',
                text: 'Hi, I’m from Acme. My dad’s just come out of hospital and I don’t know where to start.',
              },
              {
                from: 'keni',
                text: 'You’re in the right place. Let’s talk it through today. Are you free at 2?',
              },
            ],
          },
          {
            kind: 'push',
            title: 'Call booked',
            body: `${EXPERT.name}, today at 2pm.`,
            when: 'now',
          },
          { kind: 'call' },
        ],
      },
      {
        layout: 'call',
        pieces: [
          { kind: 'call' },
          {
            kind: 'bubbles',
            lines: [
              {
                from: 'user',
                text: 'Work said you could help with my dad’s care.',
              },
              {
                from: 'keni',
                text: 'We can. Shall we start with a quick call?',
              },
            ],
            replies: ['Today at 2', 'Tomorrow at 10'],
          },
          {
            kind: 'push',
            title: 'Good news',
            body: 'You could be eligible for over £5,000 a year.',
            when: 'now',
          },
        ],
      },
      {
        layout: 'cascade',
        pieces: [
          {
            kind: 'chat',
            lines: [
              {
                from: 'user',
                text: 'I’ve been told no twice now.',
              },
              {
                from: 'keni',
                text: 'This happens a lot, don’t worry. Here’s what we’ll do.',
              },
            ],
            typing: true,
          },
          {
            kind: 'push',
            title: 'It’s been approved',
            body: `${EXPERT.name} got a response on your appeal.`,
            when: 'now',
          },
          {
            kind: 'reminder',
            day: '21',
            month: 'Oct',
            title: `Call with ${EXPERT.name}`,
            time: 'Today at 2pm',
          },
        ],
      },
    ],
  },
  {
    id: 'banks',
    label: 'Banks and building societies',
    variants: [
      {
        layout: 'classic',
        pieces: [
          {
            kind: 'chat',
            lines: [
              {
                from: 'user',
                text: 'I need some financial help with my mum’s care. My bank told me to come to you.',
              },
              {
                from: 'keni',
                text: 'I’m glad they did. Let’s look at what you’re entitled to first.',
              },
            ],
          },
          FUNDING_APPROVED,
          { kind: 'call' },
        ],
      },
      {
        layout: 'call',
        pieces: [
          { kind: 'call' },
          {
            kind: 'bubbles',
            lines: [
              {
                from: 'user',
                text: 'My bank said you could help me plan for care costs.',
              },
              {
                from: 'keni',
                text: 'Let’s get you the help you deserve. I’ll walk you through it now.',
              },
            ],
          },
          FUNDING_APPROVED,
        ],
      },
      {
        layout: 'cascade',
        pieces: [
          {
            kind: 'chat',
            lines: [
              {
                from: 'keni',
                text: 'You don’t need to sell the house. Let’s make a financial plan.',
              },
            ],
            offer: 'See my plan',
          },
          {
            kind: 'push',
            title: 'Your care funding plan is ready',
            body: 'Everything you could claim, in one place.',
            when: 'now',
          },
          {
            kind: 'reminder',
            day: '23',
            month: 'Oct',
            title: `Plan review with ${EXPERT.name}`,
            time: 'Thursday at 11am',
          },
        ],
      },
    ],
  },
  {
    id: 'insurers',
    label: 'Insurers',
    variants: [
      {
        layout: 'classic',
        pieces: [
          {
            kind: 'chat',
            lines: [
              {
                from: 'user',
                text: 'I’m here through my insurance. I’ve been off work with stress and need help caring for my mum.',
              },
              {
                from: 'keni',
                text: 'That’s a lot to carry. We’ll take some of it off you, starting today.',
              },
            ],
          },
          CALL_SOON,
          { kind: 'call' },
        ],
      },
      {
        layout: 'call',
        pieces: [
          { kind: 'call' },
          {
            kind: 'bubbles',
            lines: [
              {
                from: 'user',
                text: 'My insurer said you could help with my mum’s care.',
              },
              {
                from: 'keni',
                text: 'We can. You don’t have to do this on your own.',
              },
            ],
          },
          {
            kind: 'push',
            title: 'Call booked',
            body: `${EXPERT.name}, tomorrow at 10am.`,
            when: 'now',
          },
        ],
      },
      {
        layout: 'cascade',
        pieces: [
          {
            kind: 'chat',
            lines: [
              {
                from: 'keni',
                text: 'That’s a lot to carry. Shall we talk through your support options?',
              },
            ],
            replies: ['Yes, today', 'Later this week'],
          },
          {
            kind: 'push',
            title: 'Sam joined your care circle',
            body: 'You can now share tasks and updates about Mum.',
            when: 'now',
          },
          CALL_SOON,
        ],
      },
    ],
  },
  {
    id: 'advisers',
    label: 'Financial advisers',
    variants: [
      {
        layout: 'classic',
        pieces: [
          {
            kind: 'chat',
            lines: [
              {
                from: 'user',
                text: 'My financial adviser told me to come to you about paying for care.',
              },
              {
                from: 'keni',
                text: 'Great. I’ll work alongside them, so you get one plan, not two.',
              },
            ],
          },
          {
            kind: 'push',
            title: 'Your care funding plan is ready',
            body: 'Shared with you and your adviser.',
            when: 'now',
          },
          { kind: 'call' },
        ],
      },
      {
        layout: 'call',
        pieces: [
          { kind: 'call' },
          {
            kind: 'bubbles',
            lines: [
              { from: 'user', text: 'My adviser sent me your way.' },
              {
                from: 'keni',
                text: 'Welcome. I’ll work alongside them, so you get one plan.',
              },
            ],
          },
          FUNDING_APPROVED,
        ],
      },
      {
        layout: 'cascade',
        pieces: [
          {
            kind: 'chat',
            lines: [
              {
                from: 'keni',
                text: 'I’ve read the care home contract. There are two things to raise before you sign.',
              },
            ],
            offer: `Talk it through with ${EXPERT.name}`,
          },
          {
            kind: 'push',
            title: 'Documents checked',
            body: 'All 3 are in. Nothing else needed from you.',
            when: 'now',
          },
          {
            kind: 'reminder',
            day: '23',
            month: 'Oct',
            title: 'Visit Oakwood House',
            time: 'In 1 hour',
          },
        ],
      },
    ],
  },
];

// ── 2. Homepage services carousel: single pullouts ────────────────────────

export const HOME_SERVICES: Picture[] = [
  {
    id: 'funding',
    label: 'Funding advice and appeals',
    pieces: [FUNDING_APPROVED],
  },
  { id: 'care-search', label: 'Care search', pieces: [HOMES_FOUND] },
  {
    id: 'poa',
    label: 'Power of attorney',
    pieces: [
      {
        kind: 'chat',
        lines: [
          {
            from: 'keni',
            text: 'Have you set up power of attorney yet? I can do the application with you.',
          },
        ],
        replies: ['Yes please', 'What is it?'],
      },
    ],
  },
  { id: 'assessment', label: 'Care assessment', pieces: [CALL_SOON] },
  {
    id: 'emergency',
    label: 'Emergency support',
    pieces: [
      {
        kind: 'chat',
        lines: [
          {
            from: 'keni',
            text: 'We got your message. We can jump on a call this morning if you’re free at 10.',
          },
        ],
      },
    ],
  },
  {
    id: 'expert',
    label: 'Dedicated care expert',
    pieces: [
      {
        kind: 'chat',
        lines: [
          {
            from: 'keni',
            text: `Hi, I’m ${EXPERT.name}, your care expert. Shall we set up a call?`,
          },
        ],
        replies: ['Today at 2', 'Tomorrow at 10'],
      },
    ],
  },
  {
    id: 'resources',
    label: 'Resources and wellbeing',
    pieces: [
      {
        kind: 'push',
        title: 'Starting in 10 minutes',
        body: 'Webinar: Understanding care funding in the UK.',
        when: 'now',
      },
    ],
  },
];

// ── 3. Services page carousel: clusters ───────────────────────────────────

export const SERVICE_CLUSTERS: Picture[] = [
  {
    id: 'funding-review',
    label: 'Funding review, advice and appeals',
    pieces: [
      {
        kind: 'chat',
        lines: [
          {
            from: 'keni',
            text: 'I’ve found two changes that should get your claim approved. Can I talk you through them?',
          },
        ],
      },
      FUNDING_APPROVED,
    ],
  },
  {
    id: 'assessment',
    label: 'Live care assessment and care plans',
    pieces: [
      {
        kind: 'push',
        title: 'We’ve got your GP letter',
        body: 'Book your care assessment for next week.',
        when: '1h ago',
      },
      CALL_SOON,
      { kind: 'button', label: 'Book assessment' },
    ],
  },
  {
    id: 'placements',
    label: 'Vetted care placements',
    pieces: [
      HOMES_FOUND,
      {
        kind: 'reminder',
        day: '23',
        month: 'Oct',
        title: 'Visit Oakwood House',
        time: 'In 1 hour',
      },
    ],
  },
  {
    id: 'poa',
    label: 'Same-day power of attorney',
    pieces: [
      {
        kind: 'chat',
        lines: [
          {
            from: 'keni',
            text: 'Thanks for your time earlier. I’ve sent off the power of attorney application for you.',
          },
        ],
      },
      {
        kind: 'push',
        title: 'Application submitted',
        body: 'Lasting power of attorney, health and welfare.',
        when: 'now',
      },
    ],
  },
  {
    id: 'discharge',
    label: 'Hospital discharge support',
    pieces: [
      {
        kind: 'chat',
        lines: [
          {
            from: 'keni',
            text: 'I’ve found two carers near you who can help when your dad comes home.',
          },
        ],
      },
      {
        kind: 'reminder',
        day: '24',
        month: 'Oct',
        title: 'Dad home from hospital',
        time: 'Carer arrives at 11am',
      },
    ],
  },
  {
    id: 'contracts',
    label: 'Care contract reviews',
    pieces: [
      {
        kind: 'chat',
        lines: [
          {
            from: 'keni',
            text: 'Free to jump on a call? There are a couple of red flags in the contract.',
          },
        ],
      },
      { kind: 'call' },
    ],
  },
];

// ── 4. Sector accordions: single pullouts ─────────────────────────────────

const say = (text: string): Piece => ({
  kind: 'chat',
  lines: [{ from: 'keni', text }],
});

export const EMPLOYER_PULLOUTS: Picture[] = [
  {
    id: 'funding',
    label: 'Funding',
    pieces: [say('Good news. You could be eligible for over £5,000 a year.')],
  },
  {
    id: 'guidance',
    label: 'Expert guidance',
    pieces: [
      {
        kind: 'bubbles',
        lines: [
          { from: 'user', text: 'They’ve turned us down again.' },
          {
            from: 'keni',
            text: 'This happens a lot, don’t worry. Here’s what we’ll do.',
          },
        ],
      },
    ],
  },
  {
    id: 'care-homes',
    label: 'Care home sourcing',
    pieces: [
      {
        kind: 'push',
        title: 'New matches for your mum',
        body: 'Here are the homes we found that meet their needs.',
        when: 'now',
      },
    ],
  },
  {
    id: 'wellbeing',
    label: 'Wellbeing support',
    pieces: [
      {
        kind: 'chat',
        lines: [
          {
            from: 'keni',
            text: 'That’s a lot to carry. Shall we talk through your support options?',
          },
        ],
        replies: ['Yes, today', 'Later this week'],
      },
    ],
  },
  {
    id: 'financial-plan',
    label: 'Financial planning',
    pieces: [
      say('You don’t need to sell the house. Let’s make a financial plan.'),
    ],
  },
  {
    id: 'advocate',
    label: 'Advocacy',
    pieces: [
      {
        kind: 'bubbles',
        lines: [
          { from: 'keni', text: 'We’ve had a response. It’s been approved!' },
          { from: 'user', text: 'I can’t believe it. Thank you!' },
        ],
      },
    ],
  },
];

export const BANK_PULLOUTS: Picture[] = [
  {
    id: 'valued',
    label: 'Supporting valued customers',
    pieces: [say('Let’s get you the help you deserve.')],
  },
  {
    id: 'welcome',
    label: 'Getting started',
    pieces: [
      {
        kind: 'chat',
        lines: [
          {
            from: 'keni',
            text: `Welcome to KareHero. I’m ${EXPERT.name}, and I’ll be with you the whole way.`,
          },
        ],
        replies: ['Let’s get started'],
      },
    ],
  },
  {
    id: 'vulnerable',
    label: 'Vulnerable customers',
    pieces: [
      say(
        'That’s not right, and I’m sorry you were told that. Here’s what you’re eligible for.',
      ),
    ],
  },
  {
    id: 'care-circle',
    label: 'Family care circle',
    pieces: [
      {
        kind: 'push',
        title: 'Sam joined your care circle',
        body: 'You can now share tasks and updates about Mum.',
        when: 'now',
      },
    ],
  },
];

/** Chloe's general asks, under the sector stickies. */
export const GENERAL_PULLOUTS: Picture[] = [
  {
    id: 'face',
    label: 'New activity with your expert',
    pieces: [
      {
        kind: 'face',
        title: `${EXPERT.name} sent you a message`,
        body: 'I’ve got an update on the claim.',
      },
    ],
  },
  {
    id: 'incoming',
    label: 'Incoming call',
    pieces: [{ kind: 'incoming' }],
  },
  {
    id: 'processed',
    label: 'Something finished processing',
    pieces: [
      {
        kind: 'push',
        title: 'Documents checked',
        body: 'All 3 are in. Nothing else needed from you.',
        when: 'now',
      },
    ],
  },
  {
    id: 'event',
    label: 'A life event we help with',
    pieces: [
      {
        kind: 'reminder',
        day: '28',
        month: 'Oct',
        title: 'Hospital discharge meeting',
        time: '2pm, St Thomas’ Hospital',
      },
    ],
  },
];

/** Section headings on the page, with the Miro sticky they answer. */
export const SECTIONS = {
  banners: {
    title: 'Main banners',
    body: 'Home and pillar pages, three versions each. Features in front of a blurred app.',
  },
  homeServices: {
    title: 'Homepage services carousel',
    body: 'Single pullouts on a clear background.',
  },
  serviceClusters: {
    title: 'Services page carousel',
    body: 'Clusters on a clear background, kept close together.',
  },
  sectors: {
    title: 'Sector accordions',
    body: 'Single pullouts on a clear background.',
  },
  employers: 'Employers',
  banks: 'Banks',
  general: 'General',
} as const;

/** Labels in the incoming call screen. */
export const INCOMING = {
  label: 'KareHero care expert',
  decline: 'Decline',
  accept: 'Accept',
} as const;
